import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { assessmentService } from "../services/assessmentService";
import type { Assessment, AttemptResult } from "../types/assessment";
import { companyService } from "@/features/company/services/companyService";
import { visaService } from "@/features/profile/services/visaService";

const COUNTRY_CODES = ["JP","PH","VN","ID","MM","KH","TH","CN","NP","IN","BD","LK"];

const SCORE_BUCKETS = [
  "0–10%","10–20%","20–30%","30–40%","40–50%",
  "50–60%","60–70%","70–80%","80–90%","90–100%",
];

function scoreToIndex(score: number | null): number {
  if (score == null) return -1;
  return Math.min(9, Math.floor(score / 10));
}

function SelectFilter({
  label,
  value,
  onChange,
  options,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder: string;
}) {
  return (
    <div className="flex-1 min-w-[140px]">
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white text-gray-700"
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

interface Filters {
  country: string;
  company: string;
  batch: string;
  visa_type: string;
}

export function AssessmentResultsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [allAssessments, setAllAssessments] = useState<Assessment[]>([]);
  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [results, setResults] = useState<AttemptResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [latestOnly, setLatestOnly] = useState(false);

  const [companyOptions, setCompanyOptions] = useState<{ value: string; label: string }[]>([]);
  const [visaTypeOptions, setVisaTypeOptions] = useState<{ value: string; label: string }[]>([]);

  const [filters, setFilters] = useState<Filters>({ country: "", company: "", batch: "", visa_type: "" });
  const [applied, setApplied] = useState<Filters>({ country: "", company: "", batch: "", visa_type: "" });

  useEffect(() => {
    assessmentService.list().then(setAllAssessments).catch(console.error);
    companyService.getDropdown().then(setCompanyOptions).catch(console.error);
    visaService.getVisaDropdown(i18n.language || "en").then(setVisaTypeOptions).catch(console.error);
  }, []);

  const fetchData = async (activeFilters: Filters) => {
    if (!id) return;
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (activeFilters.country)   params.country   = activeFilters.country;
      if (activeFilters.company)   params.company   = activeFilters.company;
      if (activeFilters.batch)     params.batch     = activeFilters.batch;
      if (activeFilters.visa_type) params.visa_type = activeFilters.visa_type;

      const [a, r] = await Promise.all([
        assessment?.assessment_id === id ? Promise.resolve(assessment) : assessmentService.get(id),
        assessmentService.getResults(id, params),
      ]);
      setAssessment(a);
      setResults(r);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setAssessment(null);
    fetchData(applied);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleApply = () => {
    setApplied(filters);
    fetchData(filters);
  };

  const handleClear = () => {
    const empty: Filters = { country: "", company: "", batch: "", visa_type: "" };
    setFilters(empty);
    setApplied(empty);
    fetchData(empty);
  };

  const handleSelectAssessment = (newId: string) => {
    if (newId && newId !== id) {
      navigate(`/assessments/${newId}/results`);
    }
  };

  // Latest attempt only: keep the most recent attempt per user
  const displayResults = latestOnly
    ? Object.values(
        results.reduce((acc, r) => {
          const prev = acc[r.user_id];
          if (!prev || r.started_at > prev.started_at) acc[r.user_id] = r;
          return acc;
        }, {} as Record<string, AttemptResult>)
      )
    : results;

  // ── Summary stats ──────────────────────────────────────────────────────────

  const completed = displayResults.filter((r) => r.status === "completed");
  const uniqueParticipants = new Set(displayResults.map((r) => r.user_id)).size;
  const avgScore =
    completed.length > 0
      ? Math.round(completed.reduce((s, r) => s + (r.score ?? 0), 0) / completed.length)
      : null;
  const passRate =
    completed.length > 0
      ? Math.round((completed.filter((r) => r.passed).length / completed.length) * 100)
      : null;

  // ── Score distribution ─────────────────────────────────────────────────────

  const distribution = Array(10).fill(0) as number[];
  completed.forEach((r) => {
    const idx = scoreToIndex(r.score);
    if (idx >= 0) distribution[idx]++;
  });
  const maxBucket = Math.max(...distribution, 1);

  const countryOptions = COUNTRY_CODES.map((code) => ({
    value: code,
    label: t(`profile.country_${code.toLowerCase()}`, { defaultValue: code }),
  }));

  const exportCSV = (rows: AttemptResult[], title?: string) => {
    const header = ["Assessment Title","Name","Country","Company","Batch","Visa Type","Score (%)","Result","Completed At"];
    const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const lines = rows.map((r) => [
      escape(title ?? ""),
      escape(r.full_name),
      escape(r.country ? t(`profile.country_${r.country.toLowerCase()}`, { defaultValue: r.country }) : ""),
      escape(r.company_name ?? ""),
      escape(r.batch_no ?? ""),
      escape(r.visa_type ?? ""),
      r.score != null ? String(r.score) : "",
      r.status !== "completed" ? r.status : r.passed ? "Passed" : "Failed",
      escape(r.completed_at ? new Date(r.completed_at).toLocaleString() : ""),
    ].join(","));
    const csv = [header.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(title ?? "results").replace(/\s+/g, "_")}_results.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <button
            onClick={() => navigate("/assessments")}
            className="text-sm text-gray-400 hover:text-gray-600 mb-1 flex items-center gap-1"
          >
            ← {t("assessment.back_to_list")}
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            {assessment?.title ?? t("assessment.results")}
          </h1>
          <p className="text-sm text-gray-500 mt-1">{t("assessment.results_subtitle")}</p>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: t("assessment.stat_attempts"),    value: displayResults.length },
          { label: t("assessment.stat_participants"), value: uniqueParticipants },
          { label: t("assessment.stat_avg_score"),    value: avgScore != null ? `${avgScore}%` : "—" },
          { label: t("assessment.stat_pass_rate"),    value: passRate != null ? `${passRate}%` : "—" },
        ].map(({ label, value }) => (
          <div key={label} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">{label}</p>
            <p className="text-2xl font-bold text-gray-900">{value}</p>
          </div>
        ))}
      </div>

      {/* Filter bar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
        <div className="flex flex-wrap items-end gap-3">
          {/* Title — assessment selector */}
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t("assessment.field_title")}
            </label>
            <select
              value={id ?? ""}
              onChange={(e) => handleSelectAssessment(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white text-gray-700"
            >
              {allAssessments.map((a) => (
                <option key={a.assessment_id} value={a.assessment_id}>
                  {a.title}
                </option>
              ))}
            </select>
          </div>

          {/* Country */}
          <SelectFilter
            label={t("assessment.field_audience_country")}
            value={filters.country}
            onChange={(v) => setFilters((prev) => ({ ...prev, country: v }))}
            options={countryOptions}
            placeholder={t("access.filter_all")}
          />

          {/* Company */}
          <SelectFilter
            label={t("assessment.filter_company")}
            value={filters.company}
            onChange={(v) => setFilters((prev) => ({ ...prev, company: v }))}
            options={companyOptions}
            placeholder={t("access.filter_all")}
          />

          {/* Batch */}
          <div className="flex-1 min-w-[120px]">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t("assessment.filter_batch")}
            </label>
            <input
              type="text"
              value={filters.batch}
              onChange={(e) => setFilters((prev) => ({ ...prev, batch: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="—"
            />
          </div>

          {/* Visa Type */}
          <SelectFilter
            label={t("assessment.filter_visa_type")}
            value={filters.visa_type}
            onChange={(v) => setFilters((prev) => ({ ...prev, visa_type: v }))}
            options={visaTypeOptions}
            placeholder={t("access.filter_all")}
          />

          <div className="flex gap-2 pb-0.5">
            <button
              onClick={handleApply}
              className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-1.5 rounded-lg transition-colors"
            >
              {t("assessment.filter_apply")}
            </button>
            <button
              onClick={handleClear}
              className="text-sm text-gray-500 hover:text-gray-700 font-medium px-3 py-1.5 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
            >
              {t("common.reset")}
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="text-gray-400 text-sm">{t("common.loading")}</div>
        </div>
      ) : (
        <>
          {/* Score distribution */}
          {completed.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
              <h2 className="text-sm font-semibold text-gray-800 mb-4">
                {t("assessment.score_distribution")}
                {assessment && (
                  <span className="ml-2 text-gray-400 font-normal">— {assessment.title}</span>
                )}
              </h2>
              <div className="flex items-end gap-1.5 h-32">
                {distribution.map((count, i) => {
                  const heightPct = (count / maxBucket) * 100;
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-[10px] text-gray-400">
                        {count > 0 ? count : ""}
                      </span>
                      <div
                        className="w-full bg-indigo-400 rounded-t-sm transition-all"
                        style={{ height: `${Math.max(heightPct, count > 0 ? 4 : 0)}%` }}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-1.5 mt-1">
                {SCORE_BUCKETS.map((b) => (
                  <div key={b} className="flex-1 text-[9px] text-gray-400 text-center leading-tight">
                    {b}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Results table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            {/* Table toolbar */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <p className="text-sm font-semibold text-gray-800">
                {assessment?.title ?? t("assessment.results")}
                <span className="ml-2 text-xs font-normal text-gray-400">
                  ({displayResults.length})
                </span>
              </p>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <span className="text-xs text-gray-500">{t("assessment.latest_only")}</span>
                  <button
                    type="button"
                    onClick={() => setLatestOnly((v) => !v)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                      latestOnly ? "bg-indigo-600" : "bg-gray-200"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                        latestOnly ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                </label>
                <button
                  onClick={() => exportCSV(displayResults, assessment?.title)}
                  className="flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-indigo-600 border border-gray-200 hover:border-indigo-300 px-3 py-1.5 rounded-lg transition-colors"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  {t("assessment.export_csv")}
                </button>
              </div>
            </div>

            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  {[
                    t("assessment.col_title"),
                    t("assessment.col_name"),
                    t("assessment.col_country"),
                    t("assessment.col_company"),
                    t("assessment.col_batch"),
                    t("assessment.col_visa_type"),
                    t("assessment.col_score"),
                    t("assessment.col_result"),
                    t("assessment.col_completed_at"),
                  ].map((h) => (
                    <th
                      key={h}
                      className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {displayResults.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-4 py-10 text-center text-sm text-gray-400">
                      {t("assessment.no_results")}
                    </td>
                  </tr>
                )}
                {displayResults.map((r) => (
                  <tr key={r.attempt_id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-gray-700">{assessment?.title ?? "—"}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{r.full_name}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {r.country
                        ? t(`profile.country_${r.country.toLowerCase()}`, { defaultValue: r.country })
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{r.company_name ?? "—"}</td>
                    <td className="px-4 py-3 text-gray-600">{r.batch_no ?? "—"}</td>
                    <td className="px-4 py-3 text-gray-600">{r.visa_type ?? "—"}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {r.score != null ? `${r.score}%` : "—"}
                    </td>
                    <td className="px-4 py-3">
                      {r.status !== "completed" ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                          {r.status}
                        </span>
                      ) : r.passed ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
                          {t("assessment.passed")}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-600">
                          {t("assessment.failed")}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-xs">
                      {r.completed_at
                        ? new Date(r.completed_at).toLocaleString()
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
